/**
 * Shared helpers for the Cached Performance tab components (CR-4751).
 */
import type {
  MetricType, MetricUnit, TrendPoint, DashboardSnapshot,
} from '../../../lib/api/dashboard-snapshots'

export const METRIC_LABELS: Record<MetricType, string> = {
  open_order_value: 'Open Order Value',
  total_spend: 'Total Spend',
  monthly_spend: 'Monthly Spend',
  arrears_value: 'Arrears Value',
  arrears_count: 'Arrears Count',
  arrears_opening_value: 'Arrears Opening',
  arrears_new_value: 'New Arrears',
  arrears_resolved_value: 'Resolved Arrears',
  arrears_closing_value: 'Arrears Closing',
  otif_percentage: 'OTIF %',
  otd_percentage: 'OTD %',
  on_time_count: 'On-Time Count',
  early_count: 'Early Count',
  late_count: 'Late Count',
  total_grns: 'Total GRNs',
  ppm: 'PPM',
  ncr_count: 'NCR Count',
  ncr_open_count: 'Open NCRs',
  active_customer_count: 'Active Customers',
  appeals_count: 'Appeals',
}

export function asNumber(v: string | number | null | undefined): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

export function percentDelta(current: number, previous: number): number | null {
  if (previous === 0 || Number.isNaN(previous)) return null
  return ((current - previous) / previous) * 100
}

export function formatValueByUnit(
  value: string | number | null | undefined,
  unit: MetricUnit,
  formatCurrency: (n: number) => string,
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return '—'
  if (unit === 'currency') return formatCurrency(n)
  if (unit === 'percentage') return `${n.toFixed(1)}%`
  if (unit === 'ppm') return `${Math.round(n).toLocaleString()} ppm`
  return Math.round(n).toLocaleString()
}

export interface ChartPoint {
  month: string
  value: number
}

type Granularity = 'daily' | 'weekly' | 'monthly' | 'quarterly'

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Period-aware label so the x-axis doesn't collapse multiple points in the
 * same month to the same text. Chart axes use a fixed compact format
 * independent of user locale (locale-aware date display lives in formatDate
 * via PreferencesContext).
 *   daily     → "23 Apr"
 *   weekly    → "13 Apr" (Monday of the week)
 *   monthly   → "Feb 26"
 *   quarterly → "Q1 26"
 */
export function formatChartLabel(iso: string, period: Granularity): string {
  const d = new Date(iso)
  const day = String(d.getDate()).padStart(2, '0')
  const month = SHORT_MONTHS[d.getMonth()]
  const yr = String(d.getFullYear()).slice(2)
  switch (period) {
    case 'daily':
    case 'weekly':
      return `${day} ${month}`
    case 'monthly':
      return `${month} ${yr}`
    case 'quarterly': {
      const q = Math.floor(d.getMonth() / 3) + 1
      return `Q${q} ${yr}`
    }
  }
}

/** Turn a trend series into recharts-friendly rows keyed by period-aware label. */
export function toChartSeries(points: TrendPoint[], period: Granularity = 'monthly'): ChartPoint[] {
  return points.map((p) => ({
    month: formatChartLabel(p.snapshot_date, period),
    value: asNumber(p.metric_value),
  }))
}

/** Combine multiple trend series into one recharts-friendly row set (by date). */
export function mergeSeries(
  series: Array<{ key: string; points: TrendPoint[] }>,
  period: Granularity = 'monthly',
): Array<Record<string, string | number>> {
  const byDate = new Map<string, Record<string, string | number>>()
  series.forEach((s) => {
    s.points.forEach((p) => {
      const label = formatChartLabel(p.snapshot_date, period)
      const row = byDate.get(label) ?? { month: label }
      row[s.key] = asNumber(p.metric_value)
      byDate.set(label, row)
    })
  })
  return Array.from(byDate.values())
}

/** CSV escape + download helper. */
export function downloadCSV(rows: string[][], filename: string): void {
  const csv = rows
    .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * One dropdown, one axis: period granularity. The lookback window is derived
 * from the period (e.g. monthly → 12 months back, weekly → 13 weeks back) so
 * users don't have to reason about two overlapping filters.
 */
export const PERIOD_LOOKBACK_MONTHS: Record<string, number> = {
  daily: 1,      // ~30 days
  weekly: 3,     // ~13 weeks
  monthly: 12,
  quarterly: 24, // 8 quarters
}

export function monthsAgoISO(months: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() - months)
  return d.toISOString().slice(0, 10)
}

/** Build previousPoint map from a trends object. */
export function buildPreviousPointMap(
  trends: Record<MetricType, TrendPoint[]> | undefined,
): Map<MetricType, TrendPoint> {
  const map = new Map<MetricType, TrendPoint>()
  if (!trends) return map
  for (const [metric, points] of Object.entries(trends) as Array<[MetricType, TrendPoint[]]>) {
    if (points.length >= 2) map.set(metric, points[points.length - 2])
  }
  return map
}

/** Map latest snapshots by metric type. */
export function buildLatestMap(
  latest: DashboardSnapshot[] | undefined,
): Map<MetricType, DashboardSnapshot> {
  const map = new Map<MetricType, DashboardSnapshot>()
  latest?.forEach((s) => map.set(s.metric_type, s))
  return map
}
